import type { IHealthReport, IIssue, IRuleOptions, IssueCategory } from '../models/Issue';
import type { IScanResult } from '../models/ScanResult';
import { findExactDuplicates, findPossibleDuplicates } from './DuplicateRule';
import { findEmptyFiles, findPoorFilenames, findSystemFiles } from './FilenameRule';
import {
  findCatchAllFolders, findDeepNesting, findEmptyFolders, findLibraryLimits, findLongPaths, findSimilarFolders
} from './FolderRules';

export interface ICategoryInfo {
  category: IssueCategory;
  label: string;
  description: string;
}

/** Display order and wording for the health report. */
export const CATEGORIES: ICategoryInfo[] = [
  { category: 'duplicates', label: 'Duplicates', description: 'Groups of identical files stored more than once' },
  { category: 'possibleDuplicates', label: 'Possible duplicates', description: 'Similar names but different content — likely versions of the same document' },
  { category: 'poorFilenames', label: 'Poor filenames', description: 'Names that don\'t describe the file' },
  { category: 'emptyFolders', label: 'Empty folders', description: 'Folders with no files or subfolders' },
  { category: 'emptyFiles', label: 'Empty files', description: 'Files with no content (0 bytes)' },
  { category: 'systemFiles', label: 'System files', description: 'Hidden files created by macOS, Windows or Office (e.g. .DS_Store, Thumbs.db, ~$ lock files)' },
  { category: 'deepNesting', label: 'Deep nesting', description: 'Branches nested deeper than the configured limit' },
  { category: 'longPaths', label: 'Long paths', description: 'Paths close to SharePoint\'s 400-character limit' },
  { category: 'similarFolders', label: 'Similar folders', description: 'Sibling folders that appear to hold the same kind of content' },
  { category: 'catchAllFolders', label: 'Catch-all folders', description: 'Vaguely named folders like "Misc" or "Old" that collect misfiled documents' },
  { category: 'libraryLimits', label: 'Library size', description: 'Libraries large enough to slow down views and break OneDrive sync' }
];

/** Runs every deterministic rule. No AI, no network — safe to re-run on any scan. */
export function analyzeScan(scan: IScanResult, options: IRuleOptions): IHealthReport {
  const issues: IIssue[] = ([] as IIssue[]).concat(
    findExactDuplicates(scan),
    findPossibleDuplicates(scan),
    findPoorFilenames(scan),
    findEmptyFolders(scan),
    findEmptyFiles(scan),
    findSystemFiles(scan),
    findDeepNesting(scan, options),
    findLongPaths(scan, options),
    findSimilarFolders(scan),
    findCatchAllFolders(scan),
    findLibraryLimits(scan, options)
  );

  const byCategory = {} as IHealthReport['byCategory'];
  for (const info of CATEGORIES) {
    byCategory[info.category] = [];
  }
  for (const issue of issues) {
    byCategory[issue.category].push(issue);
  }

  return { issues, byCategory };
}
